
import Vue from 'vue'
import { Prop, Component } from 'vue-property-decorator'
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import ActionCard from '@/domains/global/components/ActionCard.vue'
import Timer from '@/domains/global/components/Timer.vue'
import { Invoice, Job, TranslationActivity } from '@/domains/global/models'
import JobInfoTable from '@/domains/jobs/components/Read/JobInfoTable.vue'
import JobPostageSection from '@/domains/jobs/components/Read/JobPostageSection.vue'
import CostSummary from '@/domains/jobs/components/CostSummary/CostSummary.vue'
import { consumeError } from '@/domains/global/utils'
import ActivityList from '@/domains/activities/components/ActivityList/ActivityList.vue'
import SectionHeader from '@/domains/global/components/SectionHeader.vue'
import ReactivateJobSection from '@/domains/jobs/components/Read/ReactivateJobSection.vue'
import AppLabel from '@/domains/global/components/Label.vue'
import JobMessages from '@/domains/jobs/components/JobMessages/index.vue'
import PaymentRequiredModal from '../PaymentRequiredModal.vue'

@Component({
  components: { ActionCard, AppLabel, SectionHeader, ActivityList, JobInfoTable, JobPostageSection, CostSummary, Timer, ReactivateJobSection, JobMessages, PaymentRequiredModal }
})
export default class BrokerJobDetails extends Vue {
  @Prop({ type: Object as () => Job, required: true })
  readonly job!: Job

  invoices: Invoice[] = []

  corrections: any = {}

  isReady () {
    return !!this.job?.ready
  }

  get customerLink () {
    return `${this.$store.state.config.customerFrontendUrl}/jobs/${this.job.id}`
  }

  get isDraft () {
    return this.job.status === 'draft'
  }

  get newActivityRoute() {
    return { name: 'new-activity', params: { id: this.job.id } }
  }

  get isAddTranslationVisible() {
    return this.isDraft
  }

  get translationDetailRows() {
    if (!this.job || !this.job.id) {
      return []
    }

    return [[this.job.translatorName, this.job.language]]
  }

  get customerDetailRows() {
    return [[this.job.customerName, this.job.customerEmail]]
  }

  get reviewInvoice() {
    return this.invoices.find(invoice => invoice.type === 'correction')
  }

  mounted() {
    this.getInvoices()
  }

  reloadData () {
    this.$emit('refresh')
    this.getInvoices()
  }

  async getInvoices() {
    try {
      const response = await axios.get<Invoice[]>(`jobs/${this.job.id}/invoices`)
      this.invoices = response.data
    } catch (error: any) {
      const formattedError = consumeError(error)
      if (formattedError.code !== 'SENTRY') {
        await this.$store.dispatch('error', formattedError.message)
      } else {
        Sentry.captureException(error)
      }
    }
  }

  async sendJobLink() {
    try {
      await axios.post(`jobs/${this.job.id}/send-link`)
      this.$store.dispatch('success', 'Successfully sent customer job link')
    } catch (error: any) {
      const formattedError = consumeError(error)
      if (formattedError.code !== 'SENTRY') {
        await this.$store.dispatch('error', formattedError.message)
      } else {
        Sentry.captureException(error)
      }
    }
  }

  confirmReturnFromActionRequired () {
    const modal = this.$refs.confirmReturnFromActionRequired as any
    modal.open()
  }

  editActivity(activity: TranslationActivity) {
    this.$router.push(`/jobs/${this.job.id}/activities/${activity.id}/translate`)
  }

  async returnFromActionRequired () {
    try {
      await axios.put(`/jobs/${this.job.id}/action-complete`)
      this.job.status = 'in progress'
      this.$store.dispatch('success', 'Job returned to provider')
    } catch (error: any) {
      if (error.response.status === 409) {
        this.$store.dispatch('error', 'Invalid job status')
        this.reloadData()
      } else {
        this.$store.dispatch('error', 'An error occurred returning the job to the provider')
      }
    }
  }

  confirmReview() {
    const modal = this.$refs.confirmReviewCompleteModal as any
    modal.open()
  }

  async reviewComplete() {
    try {
      this.corrections = await axios.get(`/jobs/${this.job.id}/corrections`)

      if (!this.corrections.paid && this.corrections.excess > 0) {
        this.$store.dispatch('error', 'Unable to complete review, job has corrections')  
      } else {
        this.markReviewCompleted()
      }
    } catch (error) {
      this.$store.dispatch('error', 'An error occurred checking payment status')
    }
  }

  async markReviewCompleted() {
    try {
      await axios.put(`/jobs/${this.job.id}/review-completed`)
      this.job.status = 'review compeleted'
      this.$store.dispatch('success', 'Job Reviewed')
      this.reloadData()
    } catch (error: any) {
      if (error.response.status === 422) {
        this.$store.dispatch('error', 'The full amount hasn\'t been paid yet')
      } else {
        this.$store.dispatch('error', 'An error occurred completing the review of this job')
      }
    }
  }

  async copyCustomerLink() {
    try {
      await navigator.clipboard.writeText(this.customerLink)
      this.$store.dispatch('success', 'Link copied to clipboard')
    } catch (error) {
      this.$store.dispatch('error', 'Unable to copy link to clipboard')
    }
  }

  async deleteJob() {
    try {
      await axios.delete(`/jobs/${this.job.id}`)
      this.$router.push('/dashboard')
    } catch (error) {
      this.$store.dispatch('error', 'Unable to delete this job')
    }
  }
}
